import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  InputGroup,
  Input,
  Label,
  FormGroup,
  InputGroupText,
} from "reactstrap";
import DataTable from "react-data-table-component";
import CommonCardHeader from "../../../CommonComponents/CommonCardHeader/CommonCardHeader";
import { Btn, Breadcrumbs, P, H5 } from "../../../../AbstractElements";
import { Edit, Camera } from "react-feather";

//MODALS
import UpdateEmpPersonalInfoModal from "../../../UiKits/Modal/Employee/UpdatePersonalInfo";
import UpdateAddressModal from "../../../UiKits/Modal/Employee/UpdatePersonalAddress";

//SERVICES
import {
  VIEW_EMPLOYEE_PROFILE_QUERY_KEY,
  employeeService,
} from "../../../../Services/employeeService";
import { CRUDPermissions } from "../../../../Types/Common/Access";

//TYPES
import { ViewEmployeeProfile } from "../../../../Types/Employee/EmployeeModel";

interface EmployeeProfileProps {
  EmpNo: string;
  access: CRUDPermissions;
}

const EmployeePersonal: React.FC<EmployeeProfileProps> = ({
  EmpNo,
  access,
}) => {
  const [formData, setFormData] = useState<ViewEmployeeProfile | null>(null);
  const [Addressmodal, setAddressModal] = useState(false);
  const [UpdatePersonalInfomodal, setUpdatePersonalInfomodal] = useState(false);
  const toggleAddressModal = () => setAddressModal(!Addressmodal);
  const toggleUpdatePersonalInfoModal = () =>
    setUpdatePersonalInfomodal(!UpdatePersonalInfomodal);

  const { data: responseData, isLoading: isFetching } = useQuery({
    queryKey: [VIEW_EMPLOYEE_PROFILE_QUERY_KEY, EmpNo],
    queryFn: () => employeeService.viewEmployeeProfileByEmpno(EmpNo),
    enabled: !!EmpNo,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (responseData?.data) {
      setFormData(responseData?.data);
    }
  }, [responseData?.data]);

  const fullAddress = [
    formData?.address_line1?.toUpperCase(),
    formData?.address_line2?.toUpperCase(),
    formData?.barangay?.toUpperCase(),
    formData?.city?.toUpperCase(),
    formData?.province?.toUpperCase(),
    formData?.country?.toUpperCase(),
    formData?.zip_code?.toUpperCase(),
  ]
    .map((value) => value?.toString().trim())
    .filter(Boolean)
    .join(", ");

  const mobileNo = [formData?.mobile1, formData?.mobile2]
    .map((value) => value?.toString().trim())
    .filter(Boolean)
    .join(" | ");

  return (
    <>
      <UpdateEmpPersonalInfoModal
        EmpNo={EmpNo}
        modal={UpdatePersonalInfomodal}
        toggle={toggleUpdatePersonalInfoModal}
      />
      <UpdateAddressModal
        EmpNo={EmpNo}
        modal={Addressmodal}
        toggle={toggleAddressModal}
      />
      <Row>
        <Col md="12">
          <Card className="card-absolute">
            <CommonCardHeader
              headClass="bg-primary"
              tagClass="txt-light"
              icon={<i className="icofont icofont-info-circle me-2"></i>}
              title={"Personal Information"}
            />
            <CardBody>
              <Row className="mb-3" hidden={!access.update}>
                <div className="d-flex justify-content-end">
                  <Btn color="info" onClick={toggleUpdatePersonalInfoModal}>
                    <Edit size={16} className="me-1" />
                    Update Personal Information
                  </Btn>
                </div>
              </Row>
              <Row className="mb-3">
                {/* Information */}
                <Col sm="12">
                  <Row>
                    <Col sm="12">
                      <Row>
                        <Col md="3">
                          <FormGroup>
                            <Label>First Name</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-user txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white text-color-light"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.first_name?.toUpperCase() ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup>
                            <Label>Middle Name</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-user txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.middle_name?.toUpperCase() ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup>
                            <Label>Last Name</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-user txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.last_name?.toUpperCase() ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup>
                            <Label>Suffix Name</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-user txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.suffix?.toUpperCase() ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup>
                            <Label>Birthdate</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-ui-calendar txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.birthdate
                                      ? new Date(formData.birthdate)
                                          .toLocaleDateString("en-GB", {
                                            day: "2-digit",
                                            month: "long",
                                            year: "numeric",
                                          })
                                          ?.toUpperCase()
                                      : "<i>* No Record Found * </i>"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup>
                            <Label>Gender</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-users txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.gender?.toUpperCase() ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup>
                            <Label>Civil Status</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-heart-alt txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.civil_status?.toUpperCase() ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="3">
                          <FormGroup>
                            <Label>Nationality</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-flag txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.nationality?.toUpperCase() ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label>Email Address</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-email txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.email_address ||
                                      "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label>Mobile No.</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-phone txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : mobileNo || "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label>Telephone No.</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-telephone txt-primary"></i>
                              </InputGroupText>
                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : formData?.telno || "* No Record Found *"
                                }
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="12">
                          <FormGroup>
                            <Label>Address</Label>
                            <InputGroup>
                              <InputGroupText>
                                <i className="icofont icofont-location-pin txt-primary"></i>
                              </InputGroupText>

                              <Input
                                className="bg-white"
                                disabled
                                value={
                                  isFetching
                                    ? "Loading data..."
                                    : fullAddress || "* No Record Found *"
                                }
                              />
                              <Btn
                                color="info"
                                onClick={toggleAddressModal}
                                hidden={!access.update}
                              >
                                <Edit size={16} className="me-1" />
                                Update Address
                              </Btn>
                            </InputGroup>
                          </FormGroup>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* START GOVERMENT ID */}
        <Col sm="6">
          <Card className="card-absolute">
            <CommonCardHeader
              icon={<i className="icofont icofont-card me-2"></i>}
              headClass="bg-primary"
              tagClass="txt-light"
              title={"Government IDs"}
            />
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>SSS ID</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-id-card txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.sss_id || "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>TIN</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-id-card txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.tin || "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>Pag-IBIG ID</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-id-card txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.pagibig_id || "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>UMID</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-id-card txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.umid || "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>PhilHealth ID</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-id-card txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.philhealth_id || "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>National ID</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-id-card txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.national_id || "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        {/* END GOVERMENT ID */}

        {/* START EDUCATION */}
        <Col sm="6">
          <Card className="card-absolute">
            <CommonCardHeader
              icon={<i className="icofont icofont-university me-2"></i>}
              headClass="bg-primary"
              tagClass="txt-light"
              title={"Highest Educational Attainment"}
            />
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Education</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-hat-alt txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.ed_level?.toUpperCase() ||
                              "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label>Year</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-ui-calendar txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.year || "* No Record *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="3">
                  <FormGroup>
                    <Label>Status</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-tag txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.ed_status?.toUpperCase() ||
                              "* No Record *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="12">
                  <FormGroup>
                    <Label>School</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-university txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.school_name?.toUpperCase() ||
                              "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>Course / Strand </Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-instrument txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.course?.toUpperCase() ||
                              "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label>Honor</Label>
                    <InputGroup>
                      <InputGroupText>
                        <i className="icofont icofont-certificate-alt-1 txt-primary"></i>
                      </InputGroupText>
                      <Input
                        className="bg-white"
                        disabled
                        value={
                          isFetching
                            ? "Loading data..."
                            : formData?.honor?.toUpperCase() ||
                              "* No Record Found *"
                        }
                      />
                    </InputGroup>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        {/* START EDUCATION */}
      </Row>
    </>
  );
};

export default EmployeePersonal;
